import React, { lazy, Suspense, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { env } from 'env';

import HomeBottomButtonOptions from './HomeBottomButtonOptions';
import ExitButton from './ExitButton';
import Actions from './Actions';
import ToolActions from './ToolActions';
import Teleport from './Teleport';
// import ToolBox from './ToolBox';

import { useConference } from '../DashboardContent/Dolby';
import { useMytaverseEvent } from '../../providers';

import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';
import VirtualAssistant from './VirtualAssistant/VirtualAssistant';
import ToggleScreenSharingView from './ToggleScreenSharingView';
import Participants from '@core/dashboard/components/HomeBottomBar/Participants';
import RaisedHands from '@core/dashboard/components/HomeBottomBar/RaisedHands';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from '@core/dashboard/components/DashboardTopBar/DashboardTopBarContent/AdminTools';
import VirtualAssistantAppBar from '@core/dashboard/components/HomeBottomBar/VirtualAssistantAppBar';
import PersonViewToggleButton from '@core/dashboard/components/HomeBottomBar/PersonViewToggleButton';
import HomeBottomBarProvider from './providers/HomeBottomBarProvider';
import SearchUserProvider from '@providers/SearchUserProvider';
import RaiseHandButton from '@core/dashboard/components/HomeBottomBar/RaiseHandButton';
import TrainerTools from '@features/TrainerTools';
import { useChatState } from '@core/chat';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantScopes from '@constants/scopes';
import BrandingModeNotification from '../BrandingModeNotification';

const EventSchedulerButton = lazy(() => import('@features/EventScheduler'));
const ChatButton = lazy(() => import('@core/chat/components/ChatButton'));
const AssistantButton = lazy(() => import('@core/assistant'));

const HomeBottomBar = () => {
  const { videoStarted, toggleVideo } = useConference();
  const { userPermissions } = useMytaverse();
  const {
    currentRoom,
    showExitButton,
    currentParticipant,
    currentEvent,
    isHideWebUIExceptAssistant,
    isHideAllExceptTopBar,
    skyLinkExperience,
    canSpeak,
    canShowCamera,
    canShareCamera,
  } = useMytaverseEvent();
  const { REACT_APP_CLASSROOM_EVENT_TAGS, REACT_APP_FEATURE_EVENT_SCHEDULER } =
    env;
  const CLASSROOM_TAGS = (REACT_APP_CLASSROOM_EVENT_TAGS || '')
    .split(',')
    .map((t) => t.toLowerCase().trim());
  const [overwriteAppBarIsOpen, setOverwriteAppBarIsOpen] = useState(true);
  const {
    isUnreadEventChannel,
    isUnreadPrivateChannel,
    isUnreadEventRoomChannel,
  } = useChatState();

  const showInviteUserBtn =
    userPermissions?.includes(ParticipantScopes.HubInviteParticipants) ?? false;

  const isTrainer = userPermissions?.includes(
    ParticipantScopes.HubTrainingToolHubEvent,
  );
  // Check if current event is a classroom event
  const eventTags = currentEvent?.eventTags ?? [];
  const isClassroomEvent = eventTags.some(
    (tag) =>
      typeof tag === 'string' && CLASSROOM_TAGS.includes(tag.toLowerCase()),
  );

  const showTrainerTools = isClassroomEvent && isTrainer;
  const showRaiseHandButton = isClassroomEvent && !isTrainer;
  userPermissions?.includes(ParticipantScopes.HubRiseHand);

  const showDolbyConferenceControls = !!currentRoom;

  useEffect(() => {
    return () => {
      if (videoStarted) {
        toggleVideo();
      }
    };
  }, [videoStarted]);

  if (!showDolbyConferenceControls) {
    return null;
  }

  const isHideUi =
    isHideWebUIExceptAssistant.flag || isHideAllExceptTopBar.flag;

  const isCollapsedBarWithChatNotificationOnMobile =
    (isUnreadEventChannel ||
      isUnreadPrivateChannel ||
      isUnreadEventRoomChannel) &&
    isMobileOnly &&
    !overwriteAppBarIsOpen;

  const renderBottomButtons = () => {
    if (!isMobileOnly && !overwriteAppBarIsOpen) {
      // we need to have the chat icon to be the last element of the bottom bar
      // when not on mobile, so when collapsed it is the chat icon shown.
      return (
        <>
          {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
            <Suspense fallback={null}>
              <EventSchedulerButton />
            </Suspense>
          ) : null}
          <ToggleScreenSharingView />
          <Actions />
          <SearchUserProvider>
            <Participants />
          </SearchUserProvider>
          <ToolActions />
          {!!currentRoom?.isMultiPlayer ? (
            <Suspense fallback={null}>
              <SearchUserProvider>
                <ChatButton />
              </SearchUserProvider>
            </Suspense>
          ) : null}
        </>
      );
    } else {
      if (isCollapsedBarWithChatNotificationOnMobile) {
        // if we have an unread message, are on mobile, and the bar is collapsed
        // we need to move the chat button at the end before the teleport so we can
        // show it
        return (
          <>
            {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
              <Suspense fallback={null}>
                <EventSchedulerButton />
              </Suspense>
            ) : null}
            <ToggleScreenSharingView />
            <SearchUserProvider>
              <Participants />
            </SearchUserProvider>
            <ToolActions />
            <Actions />
            {!!currentRoom?.isMultiPlayer ? (
              <Suspense fallback={null}>
                <SearchUserProvider>
                  <ChatButton />
                </SearchUserProvider>
              </Suspense>
            ) : null}
            <Teleport />
          </>
        );
      } else {
        return (
          <>
            {REACT_APP_FEATURE_EVENT_SCHEDULER === 'true' ? (
              <Suspense fallback={null}>
                <EventSchedulerButton />
              </Suspense>
            ) : null}
            <ToggleScreenSharingView />
            {!!currentRoom?.isMultiPlayer ? (
              <Suspense fallback={null}>
                <SearchUserProvider>
                  <ChatButton />
                </SearchUserProvider>
              </Suspense>
            ) : null}
            <SearchUserProvider>
              <Participants />
            </SearchUserProvider>
            <ToolActions />
            <Actions />
            {isMobileOnly && <Teleport />}
          </>
        );
      }
    }
  };

  const renderTopButtons = () => (
    <>{showInviteUserBtn ? <AdminTools /> : null}</>
  );

  if (!isMobileOnly && isHideWebUIExceptAssistant.flag) {
    return <VirtualAssistantAppBar />;
  }

  if (!isMobileOnly && isHideAllExceptTopBar.flag) {
    return null;
  }

  const isHomeBottomButtonsBarCollapsible =
    1 +
    Number(canSpeak) +
    Number(canSpeak && (canShowCamera || canShareCamera)) +
    Number(showRaiseHandButton) +
    Number(showTrainerTools);

  return (
    <HomeBottomBarProvider>
      {isHideUi ? null : skyLinkExperience ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomLeft}
          isCollapsible={false}
          sx={{
            p: 1,
          }}
        >
          <PersonViewToggleButton />
          {showRaiseHandButton && <RaiseHandButton />}
          {showTrainerTools && <TrainerTools />}
        </MytaverseAppBar>
      ) : (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopLeft
              : AppBarPositionEnum.BottomLeft
          }
          isCollapsible={
            isMobileOnly && isHomeBottomButtonsBarCollapsible > 3 ? true : false
          }
        >
          <HomeBottomButtonOptions />
        </MytaverseAppBar>
      )}
      {skyLinkExperience ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomRight}
          isCollapsible={false}
          sx={{
            p: 1.5,
          }}
        >
          {isMobileOnly || isHideAllExceptTopBar.flag ? null : (
            <>
              <VirtualAssistant
                sx={{
                  p: 0,
                  '& img': {
                    boxShadow: '0 0 8.711px 6.222px rgba(255, 255, 255, 0.50)',
                    borderRadius: '808.889px',
                    width: 36,
                    height: 36,
                  },
                }}
              />
              <AssistantButton />
            </>
          )}
        </MytaverseAppBar>
      ) : (
        <MytaverseAppBar
          position={
            isMobileOnly
              ? AppBarPositionEnum.TopRight
              : AppBarPositionEnum.BottomRight
          }
          visiblePart={
            !isMobileOnly || isHideAllExceptTopBar.flag ? null : (
              <>
                <VirtualAssistant />
                <AssistantButton />
              </>
            )
          }
          isCollapsible={
            !isMobileOnly ||
            !(isHideAllExceptTopBar.flag || isHideWebUIExceptAssistant.flag)
          }
          overwriteAppBarIsOpen={overwriteAppBarIsOpen}
          setOverwriteAppBarIsOpen={setOverwriteAppBarIsOpen}
          collapsedSize={
            isCollapsedBarWithChatNotificationOnMobile ? 112 : undefined
          }
        >
          {/*<ToolBox />*/}

          {isMobileOnly ? renderTopButtons() : null}

          {isHideUi ? null : renderBottomButtons()}

          {isMobileOnly || isHideAllExceptTopBar.flag ? null : (
            <>
              <VirtualAssistant />
              <AssistantButton />
            </>
          )}

          {isMobileOnly ? <UserAvatarButton /> : null}
        </MytaverseAppBar>
      )}
      {showExitButton.flag ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.BottomCenter}
          isCollapsible={false}
        >
          <ExitButton />
        </MytaverseAppBar>
      ) : null}
      <RaisedHands />
      <BrandingModeNotification />
    </HomeBottomBarProvider>
  );
};

export default HomeBottomBar;
